import React, { FocusEventHandler, useEffect } from "react";
import { InitialConfigType, LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { ListNode, ListItemNode } from '@lexical/list';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { $getRoot } from "lexical";
import { LexicalToolbar } from "./LexicalToolbar";

type Props = {
    value: string;
    placeholder?: string;
    onChange: (html: string) => void;
    onBlur?: FocusEventHandler<HTMLDivElement>;
};

function PlaceholderComponent({ placeholder }: { placeholder?: string }) {
    return (
      <div className="editor-placeholder text-muted">
          {placeholder || "Start typing..."}
      </div>
    );
}

function HtmlLoader({ html }: { html: string }): null {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (!html) {
            return;
        }
        
        editor.update(() => {
            const parser = new DOMParser();
            const dom = parser.parseFromString(html, "text/html");
            const nodes = $generateNodesFromDOM(editor, dom);
            const root = $getRoot();
            root.clear();
            root.append(...nodes);
        });
    }, []);

    return null;
}

function HtmlExporter({ onHtmlChange }: { onHtmlChange: (html: string) => void }) {
    const [editor] = useLexicalComposerContext();

    return (
        <OnChangePlugin
            onChange={(editorState) => {
                editorState.read(() => {
                    const html = $generateHtmlFromNodes(editor);
                    onHtmlChange(html);
                });
            }}
        />
    );
}

export default function LexicalEditor({ value, placeholder, onChange, onBlur }: Props) {
    const initialConfig: InitialConfigType = {
        namespace: "WYSIWYGEditor",
        theme: {
            paragraph: "mb-2",
            text: {
                underline: "text-decoration-underline",
                bold: "fw-bold",
                italic: "fst-italic",
            },
        },
        onError: (error: Error) => {
            console.error("Lexical error:", error);
        },
        editorState: null,
        nodes: [ListNode, ListItemNode],
    };

    return (
      <div className="p-3 border rounded">
          <LexicalComposer initialConfig={initialConfig}>
              <LexicalToolbar />
              <div
                className="position-relative border rounded p-2"
                onDragStart={(e) => e.preventDefault()}
              >
                  <RichTextPlugin
                    contentEditable={
                        <ContentEditable
                          className="form-control border-0 cursor-text"
                          onBlur={onBlur}
                          draggable={true}
                          onDragStart={(e) => e.preventDefault()}
                        />
                    }
                    placeholder={<PlaceholderComponent placeholder={placeholder} />}
                    ErrorBoundary={LexicalErrorBoundary}
                  />
                  <HistoryPlugin />
                  <ListPlugin />
                  <HtmlLoader html={value} />
                  <HtmlExporter onHtmlChange={onChange} />
              </div>
          </LexicalComposer>
      </div>
    );
}
